/* Tela 1 · Visão geral do módulo, no formato do módulo Contábil: cabeçalho, período,
   submódulos com KPIs e o que trava uma emissão hoje. */
import React, { useState } from 'react';
import { Box, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Buildings,
  CalendarBlank,
  Clock,
  FilePlus,
  ListBullets,
  MapPin,
  Plus,
  Receipt,
  ShieldCheck,
  XCircle,
} from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '../../../../../components/glass';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { useAuth } from '../../../../../providers/auth';
import { INfsePainel, INfsePendencia, NfsePeriodo } from '../../../../../types/nfseEmissao';
import { usePermittedSubmodules } from '../../../../../utils/getSubmodulesForPath';
import {
  certificadosPath,
  editarPath,
  NFSE_ACOES,
  NFSE_EMISSAO_PATH,
  NFSE_PATHS,
} from '../shared/constants';
import { Carregando, Cascata, NfsePage } from '../shared/NfsePage';
import {
  Banner,
  C,
  Card,
  CardHead,
  CountUp,
  EnvBadge,
  Eyebrow,
  IconTile,
  MONO,
  ModuleHeader,
  NButton,
  Ring,
  Segmented,
  StatusTag,
  Tone,
} from '../shared/ui';

const PERIODOS: { value: NfsePeriodo; label: string }[] = [
  { value: 'hoje', label: 'Hoje' },
  { value: '7d', label: '7 dias' },
  { value: 'mes', label: 'Mês atual' },
  { value: 'ant', label: 'Mês anterior' },
];

type Stat = {
  label: string;
  value: number;
  tone?: 'good' | 'warn' | 'bad';
  ring?: boolean;
  fmt?: (v: number) => string;
};

const STAT_COR = { good: C.success, warn: C.warning, bad: C.danger };

const mil = (v: number) =>
  v >= 1000
    ? `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
    : v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Submodulo {
  icon: React.ComponentType<any>;
  tone: Tone;
  descricao: string;
  stats: (p: INfsePainel) => Stat[];
}

// Cada submódulo com o tile, a descrição e os KPIs do protótipo. Só aparecem os que a
// pessoa tem permissão de abrir (mesma lista do menu lateral).
const SUBMODULOS: Record<string, Submodulo> = {
  [NFSE_PATHS.emitir]: {
    icon: FilePlus,
    tone: 'blue',
    descricao: 'Nota avulsa: tomador pelo CNPJ, tributos e retenções, validação e envio à Sefin',
    stats: p => [
      { label: 'Aptas', value: p.emitentes.liberados, tone: 'good' },
      { label: 'Bloqueadas', value: p.emitentes.total - p.emitentes.liberados, tone: 'warn' },
      { label: 'Rascunhos', value: p.rascunhos },
    ],
  },
  [NFSE_PATHS.notas]: {
    icon: ListBullets,
    tone: 'green',
    descricao: 'Histórico com PDF, XML, e-mail, cancelamento e substituição',
    stats: p => [
      { label: 'No período', value: p.autorizadas_periodo },
      { label: 'Valor (R$)', value: p.valor_periodo, fmt: mil },
      { label: 'Rejeitadas', value: p.rejeitadas, tone: p.rejeitadas ? 'bad' : undefined },
    ],
  },
  [NFSE_PATHS.empresas]: {
    icon: MapPin,
    tone: 'indigo',
    descricao: 'Quem pode emitir e por quê: município, código IBGE, adesão ao Emissor Nacional',
    stats: p => [
      { label: 'Carteira', value: p.emitentes.total },
      {
        label: 'Aptas',
        value: p.emitentes.total ? (p.emitentes.liberados / p.emitentes.total) * 100 : 0,
        tone: 'good',
        ring: true,
      },
      {
        label: 'Sem IBGE',
        value: p.emitentes.sem_ibge,
        tone: p.emitentes.sem_ibge ? 'warn' : undefined,
      },
    ],
  },
  [NFSE_PATHS.certificados]: {
    icon: ShieldCheck,
    tone: 'violet',
    descricao: 'Envie o .pfx e acompanhe a validade. A senha nunca aparece em tela',
    stats: p => [
      { label: 'Válidos', value: p.certificados.validos, tone: 'good' },
      { label: 'Vencendo', value: p.certificados.vencendo, tone: 'warn' },
      { label: 'Vencidos', value: p.certificados.vencidos, tone: 'bad' },
    ],
  },
};

function SubmoduleCard({
  path,
  nome,
  painel,
  index,
}: {
  path: string;
  nome: string;
  painel?: INfsePainel;
  index: number;
}) {
  const navigate = useNavigate();
  const sub = SUBMODULOS[path];
  if (!sub) return null;
  return (
    <Cascata i={index}>
      <GlassCard
        intensity="strong"
        hoverable
        preencherAltura
        p={6}
        cursor="pointer"
        role="link"
        tabIndex={0}
        onClick={() => navigate(path)}
        onKeyDown={e => e.key === 'Enter' && navigate(path)}
        _focusVisible={{ boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${C.accent}` }}
      >
        <Flex direction="column" gap={4} h="full">
          <Flex align="center" justify="space-between">
            <IconTile icon={sub.icon} tone={sub.tone} size={52} iconSize={26} />
            <ArrowRight size={18} color="#B7DAFC" weight="bold" />
          </Flex>
          <Box flex={1}>
            <Heading as="h3" fontSize="14.5px" fontWeight={600} color={C.ink} mb={1}>
              {nome}
            </Heading>
            <Text fontSize="12px" lineHeight="16px" color={C.subtle}>
              {sub.descricao}
            </Text>
          </Box>
          <SimpleGrid
            columns={3}
            spacing={3}
            pt={4}
            borderTopWidth="1px"
            borderColor="rgba(15, 23, 42, 0.06)"
          >
            {painel
              ? sub.stats(painel).map(s => (
                  <Box key={s.label}>
                    <Eyebrow fontSize="9.5px" letterSpacing="1px">
                      {s.label}
                    </Eyebrow>
                    {s.ring ? (
                      <Box mt={1}>
                        <Ring value={s.value} size={36} stroke={4} />
                      </Box>
                    ) : (
                      <Text
                        fontFamily={MONO}
                        fontSize="18px"
                        lineHeight="22px"
                        fontWeight={600}
                        color={s.tone ? STAT_COR[s.tone] : C.ink}
                        mt={1}
                      >
                        <CountUp value={s.value} format={s.fmt} />
                      </Text>
                    )}
                  </Box>
                ))
              : null}
          </SimpleGrid>
        </Flex>
      </GlassCard>
    </Cascata>
  );
}

const PENDENCIA: Record<INfsePendencia['tipo'], { icon: React.ComponentType<any>; tone: Tone }> = {
  rejeitada: { icon: XCircle, tone: 'danger' },
  rascunho: { icon: FilePlus, tone: 'neutral' },
  certificado_vencido: { icon: ShieldCheck, tone: 'danger' },
  certificado_vencendo: { icon: Clock, tone: 'warning' },
};

function PendenciaLinha({ p, onClick }: { p: INfsePendencia; onClick: () => void }) {
  const cfg = PENDENCIA[p.tipo];
  let tag = <StatusTag kind="rejeitada" />;
  if (p.tipo === 'certificado_vencido') tag = <StatusTag kind="vencido" />;
  if (p.tipo === 'certificado_vencendo') tag = <StatusTag kind="a_vencer" />;
  if (p.tipo === 'rascunho') tag = <StatusTag kind="rascunho" />;
  return (
    <Flex
      as="button"
      type="button"
      onClick={onClick}
      w="full"
      textAlign="left"
      align="center"
      gap={3}
      py={3}
      px={2}
      mx={-2}
      borderRadius="14px"
      borderTopWidth="1px"
      borderColor={C.lineSoft}
      _first={{ borderTopWidth: 0 }}
      _hover={{ bg: C.brand50 }}
      transition="background 0.2s"
    >
      <IconTile icon={cfg.icon} tone={cfg.tone} size={34} iconSize={17} />
      <Box flex={1} minW={0}>
        <Text fontSize="13.5px" fontWeight={600} color={C.ink} noOfLines={1}>
          {p.titulo}
        </Text>
        <Text fontSize="12px" color={C.subtle} noOfLines={1}>
          {p.descricao}
        </Text>
      </Box>
      {tag}
    </Flex>
  );
}

const intervalo = (p?: INfsePainel) => {
  if (!p) return '';
  const ini = new Date(p.periodo.inicio);
  const fim = new Date(new Date(p.periodo.fim).getTime() - 1);
  const f = (d: Date, ano = true) =>
    d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: ano ? 'numeric' : undefined,
      timeZone: 'America/Sao_Paulo',
    });
  if (p.periodo.chave === 'hoje') return f(fim);
  return `${f(ini, false)} → ${f(fim)}`;
};

export function NfseEmissaoPainelPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { getPainel } = useNfseEmissao();
  const [periodo, setPeriodo] = useState<NfsePeriodo>('mes');
  const submodulos = usePermittedSubmodules(NFSE_EMISSAO_PATH);

  const painelQuery = useQuery({
    queryKey: ['nfse-painel', periodo],
    queryFn: getPainel,
    placeholderData: prev => prev,
  });
  const painel = painelQuery.data;

  const abrirPendencia = (p: INfsePendencia) => {
    if (p.emissao_id) navigate(editarPath(p.emissao_id));
    else navigate(certificadosPath(p.emitente_id ?? undefined));
  };

  return (
    <NfsePage ambiente={painel?.ambiente}>
      <ModuleHeader
        eyebrow="Módulo"
        title="Emissão de NFS-e"
        icon={Receipt}
        description="Emita, consulte e cancele notas fiscais de serviço pela API Nacional (Sefin). Certificados, numeração e validações num só lugar, com tudo gravado na nossa base."
        actions={
          <>
            <EnvBadge ambiente={painel?.ambiente} />
            {hasPermission(NFSE_EMISSAO_PATH, NFSE_ACOES.emitir) && (
              <NButton icon={Plus} onClick={() => navigate(NFSE_PATHS.emitir)}>
                Nova NFS-e
              </NButton>
            )}
          </>
        }
      />

      <Flex align="center" gap="14px" wrap="wrap">
        <Segmented
          id="periodo"
          label="Período"
          value={periodo}
          onChange={setPeriodo}
          options={PERIODOS}
        />
        {painel && (
          <Flex
            align="center"
            gap={2}
            h="32px"
            px={3}
            borderRadius="999px"
            bg="rgba(255, 255, 255, 0.6)"
            color={C.muted}
            fontSize="12.5px"
            boxShadow="inset 0 0 0 1px rgba(255, 255, 255, 0.8)"
          >
            <CalendarBlank size={14} />
            {intervalo(painel)}
          </Flex>
        )}
      </Flex>

      <Box as="section" aria-labelledby="h-sub">
        <Flex align="center" gap={2} mb={4}>
          <Buildings size={16} color={C.brand600} weight="duotone" />
          <Heading as="h2" id="h-sub" fontSize="16px" fontWeight={650} color={C.ink}>
            Submódulos disponíveis
          </Heading>
          <Text fontSize="12px" color={C.subtle}>
            ({submodulos.length})
          </Text>
        </Flex>
        {painelQuery.isError && (
          <Box mb={4}>
            <Banner tone="danger" compact>
              Não foi possível carregar os números do módulo. Tente de novo em instantes.
            </Banner>
          </Box>
        )}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
          {submodulos.map((s, i) => (
            <SubmoduleCard key={s.path} path={s.path} nome={s.name} painel={painel} index={i} />
          ))}
        </SimpleGrid>
      </Box>

      <Card as="section" aria-labelledby="h-at" p="18px 22px 10px">
        <CardHead id="h-at" title="Precisa de atenção" description="O que trava uma emissão hoje" />
        {!painel && painelQuery.isLoading && <Carregando />}
        {painel && painel.pendencias.length === 0 && (
          <Text fontSize="13px" color={C.subtle} pb={3}>
            Nada travando a emissão: nenhuma rejeição pendente e todos os certificados em dia.
          </Text>
        )}
        {painel?.pendencias.map(p => (
          <PendenciaLinha
            key={`${p.tipo}-${p.emissao_id ?? p.emitente_id}`}
            p={p}
            onClick={() => abrirPendencia(p)}
          />
        ))}
      </Card>
    </NfsePage>
  );
}
